/**
 * What went wrong with a push call, said to a PERSON.
 *
 * Owner, 2026-09-18: the agent sheet showed the bare wire code "not_found" under
 * the switch. That is not an error message, it is the name of one — and this
 * particular one is not a fault at all: it means the server this app is talking
 * to does not have the push routes yet. That happens on purpose to anyone on prod
 * until the day push is promoted, so it needs its own sentence rather than a code
 * nobody outside this repository can read.
 *
 * Everything else keeps the underlying words: a registration that timed out and a
 * network that failed need different retries, and inventing one sentence for both
 * would hide which happened.
 *
 * LIFTED OUT OF PushToggle when the room checkbox needed it too. Both switches
 * call the same kind of route on the same server and fail in the same three ways;
 * a second copy would be a second place to fix the next vocabulary difference —
 * which is exactly the difference that named the original bug.
 */
/**
 * Does this server simply not have the push routes yet?
 *
 * SPLIT OUT FROM THE SENTENCE BELOW because the right response to it turned out
 * not to be a sentence at all. Owner, 2026-09-18: he opened the Members sheet on
 * app-dev while the BFF image was still building, ticked "Notify me about new
 * messages here", and got told notifications were not available. Nothing was
 * broken — the web half had shipped ahead of the server half — but he had met a
 * control that could not work, which is a worse experience than not seeing one.
 *
 * So a switch whose own READ 404s now renders nothing, exactly as it does where
 * push is unsupported. The read already happens when the sheet opens, so this
 * costs no extra request. Shipping the server before the client is still the
 * right order; this is what makes getting that order wrong invisible.
 */
export function pushRoutesMissing(e: unknown): boolean {
  if (!e) return false
  return /unknown_route|not_found|^404 on /.test(String(e instanceof Error ? e.message : e))
}

export function explainPushError(e: unknown): string {
  const msg = String(e instanceof Error ? e.message : e)
  /*
   * A SERVER WITHOUT THESE ROUTES, in whichever server's vocabulary.
   *
   * The BFF says `unknown_route`; an EDGE says `not_found` — and that difference
   * is what finally named the first bug (2026-09-18: the push calls were relaying
   * to a machine instead of reaching the BFF). Both are matched here, plus
   * bffJson's bare "<status> on <path>" fallback, because the point for the person
   * is the same in all three: this server cannot do notifications yet.
   */
  if (/unknown_route|not_found|^404 on /.test(msg)) {
    return "Notifications aren't available on this server yet."
  }
  return msg
}
